import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components';
import { cx } from '../../lib/cx';
import { formatDate, formatHours } from '../../lib/format';
import { useAuth } from '../auth/useAuth';
import { useCapability } from '../workspace/useCapability';
import { engineerRosterQueryOptions, timeEntriesQueryOptions, type TimeEntry } from './api';
import { useLogTime } from './useLogTime';

/**
 * Hours against one request: what has been logged, and a way to log more.
 *
 * The form is a set of rows rather than one entry at a time, because the
 * common case after a standup is several: two people, or one person across
 * two days. Admins and leads get a "Who" on every row and can log for anyone
 * on this client; everyone else logs their own time, and the server enforces
 * the same rule (time.service.ts).
 */

const TODAY = () => new Date().toISOString().slice(0, 10);

const CONTROL =
  'w-full min-w-0 rounded-md border border-line bg-base px-2 py-[0.3125rem] fs-12.5 text-ink focus:border-line-strong focus:outline-none';

interface DraftRow {
  readonly key: number;
  readonly userId: string;
  readonly hours: string;
  readonly occurredOn: string;
  readonly note: string;
  readonly error?: string;
}

/** Hours typed as a decimal, as whole minutes — the only unit the API takes. */
function minutesOf(row: DraftRow): number {
  return Math.round(Number(row.hours) * 60);
}

function TimeEntryRow({ entry }: { entry: TimeEntry }) {
  return (
    <li className="flex items-baseline gap-2.5 py-1.5">
      <span className="numeric w-12 flex-none fs-12.5 font-semibold text-ink">
        {formatHours(entry.minutes / 60)}
      </span>
      <span className="min-w-0 flex-1 fs-12.5">
        <span className="text-ink">{entry.userName}</span>
        <span className="text-muted"> · {formatDate(entry.occurredOn)}</span>
        {entry.note !== null && entry.note !== '' && (
          <span className="block truncate fs-12 text-ink-soft">{entry.note}</span>
        )}
        {entry.loggedByName !== null && entry.loggedByName !== entry.userName && (
          <span className="block fs-11 text-faint">Logged by {entry.loggedByName}</span>
        )}
      </span>
    </li>
  );
}

export interface RequestTimeLogProps {
  readonly workspaceId: string;
  readonly requestId: string;
  /** The card popover wants a tighter shell than the request screen's tab. */
  readonly compact?: boolean;
}

export function RequestTimeLog({ workspaceId, requestId, compact = false }: RequestTimeLogProps) {
  const entriesQuery = useQuery(timeEntriesQueryOptions(workspaceId, requestId));
  const rosterQuery = useQuery(engineerRosterQueryOptions(workspaceId));
  const log = useLogTime(workspaceId, requestId);
  const canLogForOthers = useCapability('workspace.admin');
  const myId = useAuth().user?.id ?? '';

  const entries = entriesQuery.data ?? [];
  const roster = rosterQuery.data ?? [];
  const self = roster.find((person) => person.isSelf);
  const totalHours = entries.reduce((sum, entry) => sum + entry.minutes, 0) / 60;

  const [nextKey, setNextKey] = useState(1);
  const blankRow = (key: number, userId = self?.id ?? myId): DraftRow => ({
    key,
    userId,
    hours: '',
    occurredOn: TODAY(),
    note: '',
  });
  const [rows, setRows] = useState<readonly DraftRow[]>(() => [blankRow(0)]);
  const [saving, setSaving] = useState(false);

  // Rows start as "me" — almost every entry is someone logging their own
  // hours. Filled only where nobody has chosen yet, so a lead's picks survive
  // the roster refetching.
  useEffect(() => {
    if (self === undefined) return;
    setRows((current) => current.map((row) => (row.userId === '' ? { ...row, userId: self.id } : row)));
  }, [self]);

  const update = (key: number, patch: Partial<DraftRow>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch, error: undefined } : row)),
    );

  const addRow = () => {
    // A new row copies the person and date of the one above: "and the same
    // again yesterday" and "and Sara too, today" are the two things people add.
    const last = rows.at(-1);
    setRows((current) => [
      ...current,
      { ...blankRow(nextKey, last?.userId), occurredOn: last?.occurredOn ?? TODAY() },
    ]);
    setNextKey((key) => key + 1);
  };

  const ready = (row: DraftRow) =>
    row.userId !== '' && Number.isFinite(Number(row.hours)) && minutesOf(row) > 0 && row.occurredOn !== '';
  const filled = rows.filter((row) => row.hours.trim() !== '');
  const canSubmit = filled.length > 0 && filled.every(ready) && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const failed: DraftRow[] = [];
    // One at a time, in order: each is its own row in the trail and on the
    // bill, and a failure (say, someone with no grade set) should leave the
    // rest logged and that one row in the form with its reason.
    for (const row of filled) {
      try {
        // eslint-disable-next-line no-await-in-loop -- sequential on purpose, see above
        await log.mutateAsync({
          userId: row.userId,
          minutes: minutesOf(row),
          occurredOn: row.occurredOn,
          note: row.note.trim() === '' ? undefined : row.note.trim(),
        });
      } catch (error) {
        failed.push({ ...row, error: error instanceof Error ? error.message : 'Could not log this.' });
      }
    }
    setSaving(false);
    setRows(failed.length > 0 ? failed : [blankRow(nextKey)]);
    setNextKey((key) => key + 1);
  };

  return (
    <div className={compact ? 'fs-12.5' : ''}>
      <div className="flex items-baseline gap-2">
        <span className="numeric fs-19 font-bold text-ink">{formatHours(totalHours)}</span>
        <span className="fs-12 text-muted">
          logged
          {entries.length > 0
            ? ` across ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`
            : ''}
        </span>
      </div>

      {entriesQuery.isPending ? (
        <p className="mt-2 fs-12 text-muted">Loading…</p>
      ) : entries.length > 0 ? (
        <ul className="mt-2 divide-y divide-surface-3">
          {entries.map((entry) => (
            <TimeEntryRow key={entry.id} entry={entry} />
          ))}
        </ul>
      ) : null}

      <div className="mt-4 rounded-lg bg-surface-2 p-2.5">
        <div className="space-y-3 divide-y divide-line [&>*+*]:pt-3">
          {rows.map((row, index) => (
            <div key={row.key}>
              {/* Who, how long, when — then an optional note. No type: an
                  hour takes the request's own type (see TimeService.log). */}
              <div
                className={cx(
                  'grid items-center gap-1.5',
                  canLogForOthers
                    ? 'grid-cols-[minmax(0,1fr)_4.5rem_8.75rem_1.5rem]'
                    : 'grid-cols-[4.5rem_8.75rem_minmax(0,1fr)_1.5rem]',
                )}
              >
                {canLogForOthers && (
                  <select
                    value={row.userId}
                    onChange={(event) => update(row.key, { userId: event.target.value })}
                    disabled={rosterQuery.isPending}
                    aria-label={`Who, row ${index + 1}`}
                    className={CONTROL}
                  >
                    <option value="" disabled>
                      {rosterQuery.isPending ? 'Loading…' : 'Who…'}
                    </option>
                    {roster.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                        {person.isSelf ? ' (you)' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="number"
                  step={0.25}
                  min={0}
                  placeholder="Hours"
                  value={row.hours}
                  onChange={(event) => update(row.key, { hours: event.target.value })}
                  aria-label={`Hours, row ${index + 1}`}
                  className={CONTROL}
                />
                <input
                  type="date"
                  value={row.occurredOn}
                  max={TODAY()}
                  onChange={(event) => update(row.key, { occurredOn: event.target.value })}
                  aria-label={`Date, row ${index + 1}`}
                  className={CONTROL}
                />
                {!canLogForOthers && <span />}
                {rows.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => setRows((current) => current.filter((other) => other.key !== row.key))}
                    aria-label={`Remove row ${index + 1}`}
                    className="grid size-6 place-items-center rounded-md text-faint hover:bg-surface-3 hover:text-ink"
                  >
                    <X size={13} strokeWidth={2} aria-hidden />
                  </button>
                ) : (
                  <span />
                )}
              </div>
              <input
                type="text"
                value={row.note}
                placeholder="What was it? (optional)"
                onChange={(event) => update(row.key, { note: event.target.value })}
                aria-label={`Note, row ${index + 1}`}
                className={cx(CONTROL, 'mt-1.5')}
              />
              {row.error !== undefined && (
                <p role="alert" className="mt-1 fs-11 text-danger">
                  {row.error}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 rounded-md px-1.5 py-1 fs-12 font-medium text-ink-soft hover:bg-surface-3 hover:text-ink"
          >
            <Plus size={13} strokeWidth={2} aria-hidden />
            Add row
          </button>
          <Button
            size="sm"
            variant="primary"
            className="ml-auto"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {saving ? 'Logging…' : filled.length > 1 ? `Log ${filled.length} entries` : 'Log time'}
          </Button>
        </div>
      </div>
    </div>
  );
}
